import React from "react";
import { Select, DatePicker, Button, Tooltip, Checkbox } from "antd";
import { CheckSquareOutlined, BorderOutlined } from '@ant-design/icons';
import * as tConst from "../../Common/Constants"
import { VIEW_NO_TEAMS } from "../../Common/Constants";

// Define prop types for better type safety
export interface OptionType {
  value: string;
  label: string;
}

interface ToolBarAdoProps {
  // Data
  projects: OptionType[];
  initiativeList: OptionType[];
  assigneeList: OptionType[];
  priorityDriverOptions: string[];
  tagList: OptionType[];
  areaPathList?: OptionType[];
  
  // Filters
  viewId: string | number;
  assigneeId: string;
  teamsId: string;
  quarterFilter?: any;
  selectedInitiative: string;
  selectedPriorityDriver: string;
  selectedProject: string;
  selectedTags: string[];
  selectedAreaPath?: string;
  filterMinPlanningPri: number;
  filterMaxPlanningPri: number;
  includeChildren: boolean;

  // Filter callbacks
  setTeamsId: (teamsId: string) => void;
  setFilterMinPlanningPri: (min: number) => void;
  setFilterMaxPlanningPri: (max: number) => void;
  setQuarterFilter: (date: any) => void;
  setSelectedPriorityDriver: (value: string) => void;
  setSelectedTags: (values: string[]) => void;
  setSelectedAreaPath?: (value: string) => void;
  setIncludeChildren: (include: boolean) => void;
  
  // Chart visuals
  chartHeight?: number;
  openOnClickStatus: boolean;
  showTeamsFilter?: boolean;
  setOpenOnClickStatus: (status: boolean) => void;
  useAdvancedMode: boolean; // New prop for advanced mode
  
  multiSelectMode: boolean;
  onMultiSelectModeChange: (enabled: boolean) => void;
  selectedTicketsCount: number;
  onEditMultiSelect: () => void;
  
  // Callbacks
  onHandleChartHeightChange?: (value: number | null) => void;
  projectOnChange: (option: OptionType | null) => void;
  onHideProject: (project: string) => void;
  initiaitveOnChange: (option: OptionType | null) => void;
  viewOnChange: (option: OptionType | null) => void;
  assigneeOnChange: (option: OptionType | null) => void;
  onRefreshClick: () => void;
  onLogout: () => void;
}

const ToolBarAdo: React.FC<ToolBarAdoProps> = ({ filterMinPlanningPri, filterMaxPlanningPri, ...props }) => {

  const teamFilterList = tConst.TEAMS_LIST.map((item) => {
    return { value: item.team, label: item.team };
  })
  teamFilterList.unshift({ value: VIEW_NO_TEAMS, label: VIEW_NO_TEAMS });
  
  return (
    <>
      <table width="100%">
        <tbody>
          <tr>
            <td width="18%">
              {/* Col 1 */}
              {(props.projects && props.projects.length > 0) && <label>Project</label>}
            </td>
            <td width="18%">
              {/* Col 2 */}
              {props.areaPathList && <label>Filter by Area</label>}
              {!props.areaPathList && props.useAdvancedMode && <label>Filter by Tags</label>}
            </td>
            <td width="13%">
              {/* Col 4 */}
              <label>View</label>
            </td>
            <td width="13%">
              {/* Col 5 */}
              <label>Highlight Assignee</label>
            </td>
            <td width="13%">
              {/* Col 6 */}
              {props.showTeamsFilter && <label>Highlight Team</label>}
            </td>
            <td width="13%">
              {/* Col 7 */}
              <label>Filter quarter</label>
            </td>
            <td width="8%">
              {/* Col 8 */}
              {props.showTeamsFilter && <label>Include Children</label>}
            </td>
            <td width="6%">{/* Col 9 - Refresh */}</td>
            <td width="8%">{/* Col 10 - Multi */}
              <Tooltip title={props.multiSelectMode ? "Exit multiselect mode" : "Enter multiselect mode"}>
                <Button
                  type={props.multiSelectMode ? "primary" : "default"}
                  onClick={() => props.onMultiSelectModeChange(!props.multiSelectMode)}
                  size="small"
                >
                  {props.multiSelectMode ? <CheckSquareOutlined /> : <BorderOutlined />}
                  &nbsp;{props.multiSelectMode ? "Multi ON" : "Multi"}
                </Button>
              </Tooltip>
            </td>
          </tr>
          <tr>
            {/* Row 2 */}
            <td width="18%">

              {/* We will not show the project list for timelines with defined projects */}
              {(props.projects && props.projects.length > 0) && <Select
                style={{ width: "100%" }}
                showSearch={true}
                value={props.selectedProject || undefined}
                onChange={(value) => props.projectOnChange(value ? { value, label: props.projects.find(p => p.value === value)?.label || value } : null)}
              >
                {props.projects.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
              }
            </td>
            <td width="18%">
              {/* Show Area Path selector when areaPathList is provided */}
              {props.areaPathList && props.setSelectedAreaPath && <Select
                style={{ width: "100%" }}
                showSearch={true}
                allowClear={true}
                placeholder="Select area"
                value={props.selectedAreaPath || undefined}
                onChange={(value) => props.setSelectedAreaPath(value || "")}
              >
                {props.areaPathList.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
              }
              {/* Show Tags filter when no area path list and in advanced mode */}
              {!props.areaPathList && props.useAdvancedMode && <Select
                {...({ mode: "multiple", maxTagCount: 1 } as any)}
                style={{ width: "100%" }}
                showSearch={true}
                allowClear={true}
                placeholder="Select tags"
                value={props.selectedTags}
                onChange={(values: string[]) => props.setSelectedTags(values || [])}
              >
                {props.tagList.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>}
            </td>
            <td width="13%">
              <Select
                style={{ width: "100%" }}
                showSearch={true}
                value={String(props.viewId) || undefined}
                onChange={(value) => props.viewOnChange(value ? { value: String(value), label: tConst.VIEW_SELECT_OPTION.find(opt => String(opt.value) === String(value))?.label || String(value) } : null)}
              >
                {tConst.VIEW_SELECT_OPTION.map((option) => (
                  <Select.Option key={String(option.value)} value={String(option.value)}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </td>
            {/* Assignee */}
            <td width="13%">
              <Select
                style={{ width: "100%" }}
                showSearch={true}
                allowClear={true}
                placeholder="Select assignee"
                value={props.assigneeId || undefined}
                onChange={(option) => props.assigneeOnChange(option ? { value: option, label: props.assigneeList.find(a => a.value === option)?.label || option } : null)}
              >
                {props.assigneeList.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </td>
            {/* Team */}
            <td width="13%">
              {props.showTeamsFilter && <Select
                style={{ width: "100%" }}
                showSearch={true}
                allowClear={true}
                value={props.teamsId || undefined}
                onChange={(value) => props.setTeamsId(value || "")}
              >
                {tConst.VIEW_SELECT_TEAMS_OPTION.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
              }</td>
            {/* Quarter */}
            <td width="12%">
              <DatePicker
                {...({ picker: "quarter" } as any)}
                style={{ width: "100%" }}
                placeholder="Select quarter"
                value={props.quarterFilter}
                onChange={(value) => props.setQuarterFilter(value)}
                allowClear
              />
            </td>
            {/* Include Children */}
            <td width="8%">
              {props.showTeamsFilter && <Checkbox
                checked={props.includeChildren}
                onChange={(e) => props.setIncludeChildren(e.target.checked)}
              />}
            </td>
            <td width="6%">
              {
                <Button onClick={props.onRefreshClick} size="small">Refresh</Button>
              }
            </td>
            <td width="8%">
              {props.multiSelectMode && props.selectedTicketsCount > 0 && (
                <Button
                  type="primary"
                  onClick={props.onEditMultiSelect}
                  size="small"
                >
                  Edit ({props.selectedTicketsCount})
                </Button>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default ToolBarAdo;
